import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    fetchCompanies();
  }, [showArchived]);

  const fetchCompanies = async () => {
    try {
      const url = showArchived ? '/companies?show_archived=true' : '/companies';
      const response = await api.get(url);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge badge-success">Active</span>
    ) : (
      <span className="badge badge-secondary">Inactive</span>
    );
  };

  const getCompanySizeBadge = (size) => {
    const colors = {
      startup: 'badge-info',
      small: 'badge-primary',
      medium: 'badge-warning',
      large: 'badge-success',
      enterprise: 'badge-danger'
    };
    return <span className={`badge ${colors[size] || 'badge-secondary'}`}>{size}</span>;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Companies
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🏢 Companies
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage your companies and their marketing projects
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: isDark ? '#e5e7eb' : 'var(--gray-700)',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show Archived
            </label>
            <button
              onClick={() => navigate('/companies/new')}
              className="btn btn-primary"
              title="Add Company"
              onMouseOver={(e) => e.target.style.backgroundColor = 'var(--primary-700)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'var(--primary-600)'}
            >
              ➕ Add
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        <div style={{ padding: 0, overflow: 'hidden', marginTop: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: isDark ? '#374151' : 'var(--gray-50)', borderBottom: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}` }}>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Company
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Industry
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Size
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Status
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Projects
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Created
              </th>
              <th style={{ 
                padding: '1rem', 
                textAlign: 'center', 
                fontWeight: '600', 
                color: isDark ? '#ffffff' : 'var(--gray-700)',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => (
              <tr 
                key={company.id}
                style={{ 
                  backgroundColor: isDark ? '#1f2937' : 'transparent',
                  borderBottom: index < companies.length - 1 ? `1px solid ${isDark ? '#374151' : 'var(--gray-100)'}` : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'var(--gray-25)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = isDark ? '#1f2937' : 'transparent'}
              >
                <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                  <div>
                    <div style={{ 
                      fontWeight: '600', 
                      color: isDark ? '#ffffff' : 'var(--gray-900)', 
                      marginBottom: '0.25rem',
                      fontSize: '0.9rem'
                    }}>
                      {company.name}
                    </div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: isDark ? '#9ca3af' : 'var(--gray-500)',
                      fontFamily: 'monospace'
                    }}>
                      {company.slug}
                    </div>
                    {company.description && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: isDark ? '#9ca3af' : 'var(--gray-600)',
                        marginTop: '0.5rem',
                        lineHeight: '1.4',
                        maxWidth: '300px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {company.description}
                      </div>
                    )}
                    {company.website && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <a 
                          href={company.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            color: 'var(--primary-600)', 
                            fontSize: '0.75rem',
                            textDecoration: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          🌐 {company.website}
                        </a>
                      </div>
                    )}
                  </div>
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                  <span style={{ 
                    fontSize: '0.85rem', 
                    color: isDark ? '#e5e7eb' : 'var(--gray-700)'
                  }}>
                    {company.industry || '-'}
                  </span>
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                  {getCompanySizeBadge(company.company_size)}
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                  {getStatusBadge(company.is_active)}
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                  <span style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: '600',
                    color: isDark ? '#ffffff' : 'var(--gray-900)'
                  }}>
                    {company.projects_count || 0}
                  </span>
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                  <span style={{ 
                    fontSize: '0.8rem', 
                    color: isDark ? '#9ca3af' : 'var(--gray-600)'
                  }}>
                    {new Date(company.created_at).toLocaleDateString()}
                  </span>
                </td>
                                <td style={{ padding: '1rem', verticalAlign: 'middle', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/companies/${company.id}/digital-twin`);
                      }}
                      className="btn btn-sm btn-outline"
                      style={{ 
                        backgroundColor: isDark ? '#7f1d1d' : 'var(--primary-50)', 
                        borderColor: isDark ? '#991b1b' : 'var(--primary-200)',
                        color: isDark ? '#fecaca' : 'var(--primary-700)'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = isDark ? '#991b1b' : 'var(--primary-100)'}
                      onMouseOut={(e) => e.target.style.backgroundColor = isDark ? '#7f1d1d' : 'var(--primary-50)'}
                      title="View AI-Powered Digital Twin"
                    >
                      🤖 Twin
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/companies/${company.id}/edit`);
                      }}
                      className="btn btn-sm btn-outline"
                      style={{
                        backgroundColor: isDark ? '#374151' : 'var(--gray-100)',
                        color: isDark ? '#e5e7eb' : 'var(--gray-700)',
                        borderColor: isDark ? '#4b5563' : 'var(--gray-300)'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = isDark ? '#4b5563' : 'var(--gray-200)'}
                      onMouseOut={(e) => e.target.style.backgroundColor = isDark ? '#374151' : 'var(--gray-100)'}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      
        {companies.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '4rem 3rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏢</div>
            <h3 style={{ color: 'var(--gray-600)', margin: '0 0 1rem 0', fontSize: '1.5rem' }}>No Companies Yet</h3>
            <p style={{ color: 'var(--gray-500)', marginBottom: '2rem', maxWidth: '400px', margin: '0 auto 2rem auto' }}>
              Create your first company to start organizing your marketing projects and campaigns in a structured way.
            </p>
            <button
              onClick={() => navigate('/companies/new')}
              className="btn btn-primary btn-lg"
              onMouseOver={(e) => e.target.style.backgroundColor = 'var(--primary-700)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'var(--primary-600)'}
            >
              + Create First Company
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Companies;